import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { CheckCircle2, Loader2, BookOpen } from "lucide-react";
import { toast } from "sonner";
import { Field, TextInput, TextArea, validateCommon } from "./FormControls";
import { submitEnquiry } from "../lib/api";

const EMPTY = { name: "", institution: "", designation: "", email: "", phone: "", message: "" };

export default function EnquiryModal({ program, onClose }) {
  const [values, setValues] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const open = Boolean(program);

  useEffect(() => {
    if (open) {
      setValues(EMPTY);
      setErrors({});
      setDone(false);
    }
  }, [open, program]);

  const set = (k) => (e) => setValues((v) => ({ ...v, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    const errs = validateCommon(values, ["name", "institution", "designation", "email", "phone"]);
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setSubmitting(true);
    try {
      await submitEnquiry({ ...values, program });
      setDone(true);
    } catch (err) {
      toast.error("Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto bg-white sm:max-w-lg" data-testid="enquiry-modal">
        {!done ? (
          <>
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl font-bold text-navy">Program Enquiry</DialogTitle>
              <DialogDescription className="text-sm text-slate-600">
                Tell us about your institution and our team will respond from info@casia.education.
              </DialogDescription>
            </DialogHeader>
            <div
              className="mt-2 flex items-start gap-3 rounded-md border-l-4 border-gold bg-gold-light px-4 py-3"
              data-testid="enquiry-selected-program"
            >
              <BookOpen className="mt-0.5 h-4 w-4 shrink-0 text-gold-700" aria-hidden="true" />
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-gold-700">Selected Program / Domain</p>
                <p className="text-sm font-semibold text-navy">{program}</p>
              </div>
            </div>
            <form onSubmit={onSubmit} noValidate className="mt-2 space-y-4" data-testid="enquiry-form">
              <Field label="Name" htmlFor="enquiry-name" required error={errors.name}>
                <TextInput id="enquiry-name" data-testid="enquiry-name-input" value={values.name} onChange={set("name")} autoComplete="name" />
              </Field>
              <Field label="Institution / School Name" htmlFor="enquiry-institution" required error={errors.institution}>
                <TextInput id="enquiry-institution" data-testid="enquiry-institution-input" value={values.institution} onChange={set("institution")} autoComplete="organization" />
              </Field>
              <Field label="Designation / Role" htmlFor="enquiry-designation" required error={errors.designation}>
                <TextInput id="enquiry-designation" data-testid="enquiry-designation-input" value={values.designation} onChange={set("designation")} placeholder="e.g. Principal, Academic Coordinator" />
              </Field>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Email" htmlFor="enquiry-email" required error={errors.email}>
                  <TextInput id="enquiry-email" type="email" data-testid="enquiry-email-input" value={values.email} onChange={set("email")} autoComplete="email" />
                </Field>
                <Field label="Phone" htmlFor="enquiry-phone" required error={errors.phone}>
                  <TextInput id="enquiry-phone" type="tel" data-testid="enquiry-phone-input" value={values.phone} onChange={set("phone")} autoComplete="tel" />
                </Field>
              </div>
              <Field label="Message / Requirements" htmlFor="enquiry-message" error={errors.message}>
                <TextArea id="enquiry-message" data-testid="enquiry-message-input" value={values.message} onChange={set("message")} placeholder="Optional" />
              </Field>
              <button
                type="submit"
                disabled={submitting}
                data-testid="enquiry-submit-button"
                className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-navy px-4 py-3 text-sm font-bold uppercase tracking-wider text-white transition-colors hover:bg-navy-700 disabled:opacity-60"
              >
                {submitting && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
                {submitting ? "Submitting…" : "Submit Enquiry"}
              </button>
            </form>
          </>
        ) : (
          <div className="py-6 text-center" data-testid="enquiry-success">
            <CheckCircle2 className="mx-auto h-14 w-14 text-gold" aria-hidden="true" />
            <DialogHeader className="mt-4">
              <DialogTitle className="font-serif text-2xl font-bold text-navy">Thank you for your enquiry.</DialogTitle>
              <DialogDescription className="mx-auto max-w-sm text-sm text-slate-600">
                Your enquiry for <span className="font-semibold text-navy">{program}</span> has been received. The CASIA
                team will be in touch shortly.
              </DialogDescription>
            </DialogHeader>
            <button
              type="button"
              onClick={onClose}
              data-testid="enquiry-close-button"
              className="mt-6 rounded-md bg-navy px-6 py-3 text-sm font-bold uppercase tracking-wider text-white transition-colors hover:bg-navy-700"
            >
              Close
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
